import React from 'react';
import { MapPin, Clock, Users, Star, ChevronRight, Heart, Stethoscope, Pill, Truck } from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: <MapPin className="w-8 h-8 text-primary-600" />,
      title: "Find Hospitals",
      description: "Locate nearby hospitals and medical facilities with real-time availability.",
      link: "/hospitals",
      image: "/images/h1.jpg"
    },
    {
      icon: <Stethoscope className="w-8 h-8 text-primary-600" />,
      title: "Book Consultations",
      description: "Schedule appointments with certified doctors online or in-person.",
      link: "/doctors",
      image: "/images/h2.jpg"
    },
    {
      icon: <Pill className="w-8 h-8 text-primary-500" />,
      title: "Order Medicine",
      description: "Get prescription and over-the-counter medicines delivered home.",
      link: "/pharmacies",
      image: "/images/h3.webp"
    },
    {
      icon: <Truck className="w-8 h-8 text-red-600" />,
      title: "Emergency Care",
      description: "Request immediate ambulance services with GPS tracking.",
      link: "/emergency",
      image: "/images/h4.webp"
    }
  ];

  const stats = [
    { number: "1,200+", label: "Healthcare Providers", icon: <Users className="w-5 h-5" /> },
    { number: "50,000+", label: "Patients Served", icon: <Heart className="w-5 h-5" /> },
    { number: "24/7", label: "Emergency Support", icon: <Clock className="w-5 h-5" /> },
    { number: "98%", label: "Satisfaction Rate", icon: <Star className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-8">
            <div className="relative mx-auto w-32 h-32 mb-6">
              <img 
                src="/images/h5.jpg" 
                alt="Healthcare professionals" 
                className="w-32 h-32 rounded-full object-cover border-4 border-white/30 shadow-2xl"
              />
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary-500 rounded-full border-4 border-white flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Your Health, Our
            <span className="block text-primary-200">Priority</span>
          </h1>
          
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Connect with quality healthcare in Kenya. Find hospitals, consult doctors, order medicines, and access emergency services.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a 
              href="/hospitals"
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Find Hospitals
            </a>
            <a 
              href="/emergency"
              className="border-2 border-white hover:bg-white hover:text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Emergency Help
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <img 
                src="/images/h6.jpg" 
                alt="Medical services" 
                className="w-20 h-20 rounded-full object-cover border-4 border-primary-200 shadow-lg"
              />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Healthcare Services
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need for better healthcare
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <a 
                key={index} 
                href={feature.link}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all border hover:border-primary-200 group"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-primary-100 group-hover:scale-110 transition-transform"
                  />
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="text-primary-600 font-medium flex items-center">
                  Learn More <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Get healthcare services in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16 mb-6">
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  1
                </div>
                <img 
                  src="/images/h7.webp" 
                  alt="Search" 
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full object-cover border-2 border-white shadow-md"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Search</h3>
              <p className="text-gray-600">
                Find hospitals, doctors, or pharmacies near your location
              </p>
            </div>
            
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16 mb-6">
                <div className="bg-secondary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  2
                </div>
                <img 
                  src="/images/h8.webp" 
                  alt="Book" 
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full object-cover border-2 border-white shadow-md"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Book</h3>
              <p className="text-gray-600">
                Schedule appointments or place medicine orders online
              </p>
            </div>
            
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16 mb-6">
                <div className="bg-primary-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  3
                </div>
                <img 
                  src="/images/h9.webp" 
                  alt="Care" 
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full object-cover border-2 border-white shadow-md"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Get Care</h3>
              <p className="text-gray-600">
                Receive quality healthcare services at your convenience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between text-center md:text-left">
            <div className="flex items-center mb-6 md:mb-0">
              <img 
                src="/images/h10.jpg" 
                alt="Emergency ambulance" 
                className="w-16 h-16 rounded-full object-cover border-4 border-white/30 shadow-xl mr-4"
              />
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  Medical Emergency?
                </h2>
                <p className="text-red-100 text-lg">
                  Get immediate help with our 24/7 emergency service
                </p>
              </div>
            </div>
            <a 
              href="/emergency"
              className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all flex items-center"
            >
              <Truck className="w-5 h-5 mr-2" />
              Get Help Now
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center mb-8">
            <div className="relative">
              <img 
                src="/images/h11.jpg" 
                alt="Healthcare team" 
                className="w-20 h-20 rounded-full object-cover border-4 border-primary-400/50 shadow-2xl"
              />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <Heart className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of Kenyans who trust MediGo for their healthcare needs
          </p>
          <a 
            href="/hospitals"
            className="bg-primary-600 hover:bg-primary-700 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all inline-block"
          >
            Get Started Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;